import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Slider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  LinearProgress,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  RadioGroup,
  Skeleton,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Rating,
  Stepper,
  Step,
  StepLabel,
  Pagination,
  Collapse,
  Stack,
  Fade,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  Hotel as HotelIcon,
  ViewList as ViewListIcon,
  Palette as PaletteIcon,
  Animation as AnimationIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  UploadFile as UploadFileIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { 
  COMMON_STYLES, 
  createGlassmorphism,
  createHoverEffect,
  createShimmerEffect,
  createConstructionPattern,
  createDotPattern,
  createScrollbarStyles,
  createAnimationStyles,
  SidebarScrollableLayout,
  ThemeStyles,
} from '../theme';

// Add new dialog component imports
import {
  SplitDialog,
  FeatureCard,
  UploadZone,
  DialogInfoSection,
  DialogTip,
  WizardDialog
} from '../theme/components/PageLayout';

// Add UpdateNotification import
import UpdateNotification from './dialogs/UpdateNotification';

const ThemePreview = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(30);
  const [switchValue, setSwitchValue] = useState(true);
  const [selectValue, setSelectValue] = useState('option1');
  const [toggleValue, setToggleValue] = useState('left');
  const [ratingValue, setRatingValue] = useState(4);
  const [activeStep, setActiveStep] = useState(1);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [wizardDialogOpen, setWizardDialogOpen] = useState(false);
  const [selectedFeature] = useState('local');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [updateNotificationOpen, setUpdateNotificationOpen] = useState(false);

  const sections = [
    { id: 'overview', label: 'Overview', icon: <DashboardIcon /> },
    { id: 'layouts', label: 'Page Layouts', icon: <ViewListIcon /> },
    { id: 'colors', label: 'Color Palette', icon: <PaletteIcon /> },
    { id: 'typography', label: 'Typography', icon: <CodeIcon /> },
    { id: 'components', label: 'Components', icon: <BuildIcon /> },
    { id: 'animations', label: 'Animations', icon: <AnimationIcon /> },
    { id: 'utilities', label: 'Utilities', icon: <VisibilityIcon /> },
  ];

  const ComponentShowcase = ({ title, description, children, fullWidth = false }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-1px)',
        },
        ...(fullWidth && { width: '100%' })
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          fontFamily: 'StrawberrySansDisplay',
          fontWeight: 500,
          color: 'primary.main',
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {description}
        </Typography>
      )}
      {children}
    </Paper>
  );

  const OverviewSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Hotel Manager Theme Engine
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ComponentShowcase 
            title="🎨 Multi-Variant Support"
            description="Light, Dark, and Pink theme variants with seamless switching"
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="Light Theme" variant="outlined" />
              <Chip label="Dark Theme" variant="outlined" />
              <Chip label="Pink Theme" variant="outlined" />
            </Box>
          </ComponentShowcase>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ComponentShowcase 
            title="📱 Page Scrolling & Layouts"
            description="Dual scrolling modes: document-level (default) and container-level scrolling"
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="Document Scrolling" color="primary" />
              <Chip label="Container Scrolling" color="primary" />
              <Chip label="Custom Scrollbars" color="primary" />
              <Chip label="Auto Height Detection" color="primary" />
            </Box>
          </ComponentShowcase>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ComponentShowcase 
            title="✨ Rich Component Library"
            description="Comprehensive Material-UI component overrides with animations"
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="50+ Components" color="secondary" />
              <Chip label="Glassmorphism" color="secondary" />
              <Chip label="Animations" color="secondary" />
            </Box>
          </ComponentShowcase>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ComponentShowcase 
            title="🛠 Developer Experience"
            description="Utility functions, TypeScript-ready, extensive documentation"
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="Utility Functions" variant="outlined" color="primary" />
              <Chip label="TypeScript Ready" variant="outlined" color="primary" />
              <Chip label="Modular Architecture" variant="outlined" color="primary" />
            </Box>
          </ComponentShowcase>
        </Grid>
      </Grid>

      {/* Feature Demo Cards */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ ...createHoverEffect(theme, theme.palette.primary.main) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaletteIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Custom Colors</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                User-customizable primary and secondary colors with real-time updates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ ...createGlassmorphism(theme, 0.8) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnimationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Glassmorphism</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Modern glass-like effects with backdrop blur and transparency
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ ...createConstructionPattern(theme, theme.palette.warning.main) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Patterns</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Animated construction patterns and dot backgrounds
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const LayoutsSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Page Layout System
      </Typography>
      
      <ComponentShowcase 
        title="Layout Components"
        description="Ready-to-use layout components for different page structures"
        fullWidth
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Header-Constrained Layout</Typography>
              <Box sx={{ 
                border: '2px dashed', 
                borderColor: 'primary.main', 
                p: 2, 
                borderRadius: 1,
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Content automatically matches header width constraints
                  <br />
                  <Chip label="maxWidth='xl'" size="small" color="primary" sx={{ mt: 1 }} />
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Basic Page Layout</Typography>
              <Box sx={{ 
                border: '2px dashed', 
                borderColor: 'secondary.main', 
                p: 2, 
                borderRadius: 1,
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  PageContent with auto-scrolling
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Sidebar Layout</Typography>
              <Box sx={{ display: 'flex', height: 150 }}>
                <Box sx={{ 
                  width: '30%', 
                  border: '2px dashed', 
                  borderColor: 'secondary.main', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1
                }}>
                  <Typography variant="caption">Sidebar</Typography>
                </Box>
                <Box sx={{ 
                  flex: 1, 
                  border: '2px dashed', 
                  borderColor: 'primary.main', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="caption">Main Content</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Grid Layout</Typography>
              <Grid container spacing={1} sx={{ height: 150 }}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={6} key={item}>
                    <Box sx={{ 
                      border: '2px dashed', 
                      borderColor: 'success.main', 
                      borderRadius: 1,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="caption">Card {item}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Dashboard Layout</Typography>
              <Box sx={{ height: 150 }}>
                <Box sx={{ 
                  height: '30%', 
                  border: '2px dashed', 
                  borderColor: 'info.main', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1
                }}>
                  <Typography variant="caption">Header</Typography>
                </Box>
                <Box sx={{ display: 'flex', height: '70%' }}>
                  <Box sx={{ 
                    width: '25%', 
                    border: '2px dashed', 
                    borderColor: 'secondary.main', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                  }}>
                    <Typography variant="caption">Sidebar</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    border: '2px dashed', 
                    borderColor: 'primary.main', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                  }}>
                    <Typography variant="caption">Main</Typography>
                  </Box>
                  <Box sx={{ 
                    width: '25%', 
                    border: '2px dashed', 
                    borderColor: 'warning.main', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="caption">Aside</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase 
        title="Scrollbar Styles"
        description="Custom scrollbars that match the theme colors"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Default Scrollbar</Typography>
            <Box sx={{ 
              height: 100, 
              overflow: 'auto', 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              ...createScrollbarStyles(theme, theme.palette.primary.main)
            }}>
              <Typography variant="body2">
                This is a scrollable area with the default scrollbar style. 
                It has medium width and rounded corners. The scrollbar color 
                matches the primary theme color. Scroll down to see more content.
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
                Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Thin Scrollbar</Typography>
            <Box sx={{ 
              height: 100, 
              overflow: 'auto', 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              ...createScrollbarStyles(theme, theme.palette.secondary.main, 'thin')
            }}>
              <Typography variant="body2">
                This is a scrollable area with the thin scrollbar style. 
                Perfect for smaller areas and sidebars. The scrollbar is 
                thinner and more subtle. Scroll down to see more content.
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
                Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Scrollbar</Typography>
            <Box sx={{ 
              height: 100, 
              overflow: 'auto', 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              ...createScrollbarStyles(theme, theme.palette.success.main, 'main')
            }}>
              <Typography variant="body2">
                This is a scrollable area with the main scrollbar style. 
                Larger and more prominent for main content areas. Has enhanced 
                hover and active states. Scroll down to see more content.
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. 
                Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase 
        title="Scrolling Modes"
        description="Choose between document-level scrolling (default) and container-level scrolling"
        fullWidth
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Document-Level Scrolling (Default)</Typography>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Recommended for most pages</strong><br />
                  Content scrolls at browser level when exceeding window height
                </Typography>
              </Alert>
              <Box sx={{ 
                border: '2px dashed', 
                borderColor: 'success.main', 
                p: 2, 
                borderRadius: 1,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  &lt;PageContent&gt;<br />
                  Natural document scrolling<br />
                  Better accessibility
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Container-Level Scrolling</Typography>
            <Paper sx={{ p: 2, bgcolor: 'background.default', minHeight: 200 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>For specific layouts</strong><br />
                  Content scrolls within fixed-height container
                </Typography>
              </Alert>
              <Box sx={{ 
                border: '2px dashed', 
                borderColor: 'info.main', 
                p: 2, 
                borderRadius: 1,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  &lt;PageContent scrollable&gt;<br />
                  Fixed container scrolling<br />
                  Dashboard layouts
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase 
        title="Header Width Constraints"
        description="All page content automatically respects the same width constraints as your site header"
        fullWidth
      >
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> All layout components now automatically constrain content to match your header's 
            Container maxWidth="xl" (1536px). This ensures consistent alignment across your entire application.
          </Typography>
        </Alert>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Automatic Constraint</Typography>
            <Paper sx={{ p: 2, border: '2px solid', borderColor: 'success.main', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>PageContent</strong> (constrained=true)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>AppPageLayout</strong> (constrainContent=true)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>GridPageLayout</strong> (constrained=true)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                ✅ <strong>FlexPageLayout</strong> (constrained=true)
              </Typography>
              <Typography variant="body2">
                ✅ <strong>HeaderConstrainedContent</strong> wrapper
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Manual Override</Typography>
            <Paper sx={{ p: 2, border: '2px solid', borderColor: 'warning.main', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                🔧 Set <code>constrained=false</code> to disable
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                🔧 Set <code>constrainContent=false</code> for AppPageLayout
              </Typography>
              <Typography variant="body2">
                🔧 Use <code>HeaderConstrainedContent</code> to wrap any content
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </ComponentShowcase>
    </Box>
  );

  const ColorPaletteSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Color Palette
      </Typography>
      
      <ComponentShowcase title="Primary Colors" fullWidth>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.contrastText',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                Primary
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.primary.main}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'secondary.main',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'secondary.contrastText',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                Secondary
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.secondary.main}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'success.main',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'success.contrastText',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                Success
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.success.main}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'error.main',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'error.contrastText',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                Error
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.error.main}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Background Colors" fullWidth>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 120,
                  height: 80,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.primary',
                  fontWeight: 'bold',
                }}
              >
                Paper
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.background.paper}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 120,
                  height: 80,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mx: 'auto',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.primary',
                  fontWeight: 'bold',
                }}
              >
                Default
              </Box>
              <Typography variant="caption" color="text.secondary">
                {theme.palette.background.default}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>
    </Box>
  );

  const TypographySection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Typography
      </Typography>
      
      <ComponentShowcase title="Font Families" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: 'StrawberrySansDisplay' }}>
              StrawberrySansDisplay
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Used for headings and display text
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="h1" sx={{ fontFamily: 'StrawberrySansDisplay', fontSize: '2rem' }}>
                Heading 1
              </Typography>
              <Typography variant="h2" sx={{ fontFamily: 'StrawberrySansDisplay', fontSize: '1.75rem' }}>
                Heading 2
              </Typography>
              <Typography variant="h3" sx={{ fontFamily: 'StrawberrySansDisplay', fontSize: '1.5rem' }}>
                Heading 3
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: 'StrawberrySansText' }}>
              StrawberrySansText
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Used for body text and UI elements
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Body text large - Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Body text small - Sed do eiusmod tempor incididunt ut labore.
              </Typography>
              <Typography variant="caption">
                Caption text - Et dolore magna aliqua.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Text Variants" fullWidth>
        <Box sx={{ '& > *': { mb: 2 } }}>
          <Typography variant="h1">Heading 1 - Main Page Title</Typography>
          <Typography variant="h2">Heading 2 - Section Title</Typography>
          <Typography variant="h3">Heading 3 - Subsection Title</Typography>
          <Typography variant="h4">Heading 4 - Component Title</Typography>
          <Typography variant="h5">Heading 5 - Card Title</Typography>
          <Typography variant="h6">Heading 6 - Small Title</Typography>
          <Typography variant="subtitle1">Subtitle 1 - Large subtitle</Typography>
          <Typography variant="subtitle2">Subtitle 2 - Small subtitle</Typography>
          <Typography variant="body1">Body 1 - Main body text for paragraphs and content</Typography>
          <Typography variant="body2">Body 2 - Secondary body text for descriptions</Typography>
          <Typography variant="button">BUTTON TEXT</Typography>
          <Typography variant="caption">Caption text for small labels</Typography>
          <Typography variant="overline">OVERLINE TEXT</Typography>
        </Box>
      </ComponentShowcase>
    </Box>
  );

  const ComponentsSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Components
      </Typography>
      
      <ComponentShowcase title="Buttons" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Contained Buttons</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="contained" color="primary">Primary</Button>
              <Button variant="contained" color="secondary">Secondary</Button>
              <Button variant="contained" color="success">Success</Button>
              <Button variant="contained" color="error">Error</Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Outlined Buttons</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button variant="outlined" color="primary">Primary</Button>
              <Button variant="outlined" color="secondary">Secondary</Button>
              <Button variant="outlined" color="success">Success</Button>
              <Button variant="outlined" color="error">Error</Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Text Buttons</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button color="primary">Primary</Button>
              <Button color="secondary">Secondary</Button>
              <Button color="success">Success</Button>
              <Button color="error">Error</Button>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Form Controls" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Text Fields</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Standard" variant="outlined" />
              <TextField label="Filled" variant="filled" />
              <TextField label="With Helper Text" helperText="Some important text" />
              <TextField label="Error State" error helperText="This field has an error" />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Selects & Controls</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Option</InputLabel>
                <Select value={selectValue} onChange={(e) => setSelectValue(e.target.value)}>
                  <MenuItem value="option1">Option 1</MenuItem>
                  <MenuItem value="option2">Option 2</MenuItem>
                  <MenuItem value="option3">Option 3</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={<Switch checked={switchValue} onChange={(e) => setSwitchValue(e.target.checked)} />}
                label="Switch Control"
              />
              
              <FormControlLabel
                control={<Checkbox defaultChecked />}
                label="Checkbox"
              />
              
              <Box>
                <Typography variant="body2" gutterBottom>Slider</Typography>
                <Slider
                  value={sliderValue}
                  onChange={(e, newValue) => setSliderValue(newValue)}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Data Display" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Chips & Badges</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label="Default" />
              <Chip label="Primary" color="primary" />
              <Chip label="Secondary" color="secondary" />
              <Chip label="Success" color="success" />
              <Chip label="Error" color="error" />
              <Chip label="Outlined" variant="outlined" />
              <Chip label="Deletable" onDelete={() => {}} />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Badge badgeContent={4} color="primary">
                <NotificationsIcon />
              </Badge>
              <Badge badgeContent={99} color="secondary">
                <NotificationsIcon />
              </Badge>
              <Badge variant="dot" color="error">
                <NotificationsIcon />
              </Badge>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Avatars & Rating</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Avatar>A</Avatar>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                <HotelIcon />
              </Avatar>
            </Box>
            
            <Box>
              <Typography variant="body2" gutterBottom>Rating</Typography>
              <Rating
                value={ratingValue}
                onChange={(event, newValue) => setRatingValue(newValue)}
              />
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Feedback Components" fullWidth>
        <Box sx={{ '& > *': { mb: 2 } }}>
          <Alert severity="success">This is a success alert with theme styling!</Alert>
          <Alert severity="info">This is an info alert with custom colors!</Alert>
          <Alert severity="warning">This is a warning alert with animations!</Alert>
          <Alert severity="error">This is an error alert with proper contrast!</Alert>
          
          <Box>
            <Typography variant="body2" gutterBottom>Progress Indicators</Typography>
            <LinearProgress value={60} variant="determinate" sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <CircularProgress size={24} variant="determinate" value={75} />
              <Typography variant="body2">Loading...</Typography>
            </Box>
          </Box>
        </Box>
      </ComponentShowcase>

      <ComponentShowcase title="Tables" fullWidth>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Hotel Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Rooms</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Grand Hotel</TableCell>
                <TableCell>New York</TableCell>
                <TableCell>150</TableCell>
                <TableCell>
                  <Rating value={4} readOnly size="small" />
                </TableCell>
                <TableCell>
                  <Chip label="Active" color="success" size="small" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Ocean View Resort</TableCell>
                <TableCell>Miami</TableCell>
                <TableCell>200</TableCell>
                <TableCell>
                  <Rating value={5} readOnly size="small" />
                </TableCell>
                <TableCell>
                  <Chip label="Active" color="success" size="small" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mountain Lodge</TableCell>
                <TableCell>Colorado</TableCell>
                <TableCell>75</TableCell>
                <TableCell>
                  <Rating value={3} readOnly size="small" />
                </TableCell>
                <TableCell>
                  <Chip label="Maintenance" color="warning" size="small" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </ComponentShowcase>
    </Box>
  );

  const AnimationsSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Animations & Effects
      </Typography>
      
      <ComponentShowcase title="Built-in Animations" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center',
              ...createAnimationStyles('fadeIn', '2s', 'ease-in-out'),
              '&:hover': {
                ...createAnimationStyles('fadeIn', '0.5s', 'ease-in-out')
              }
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Fade In</Typography>
              <Typography variant="body2" color="text.secondary">
                Smooth fade in animation
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center',
              '&:hover': {
                animation: 'spin 1s linear infinite'
              }
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Spin</Typography>
              <Typography variant="body2" color="text.secondary">
                Hover to see rotation
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center',
              animation: 'pulseBoxShadow 2s infinite'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Pulse Glow</Typography>
              <Typography variant="body2" color="text.secondary">
                Pulsing box shadow effect
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper sx={{ 
              p: 2, 
              textAlign: 'center',
              ...createShimmerEffect(theme)
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Shimmer</Typography>
              <Typography variant="body2" color="text.secondary">
                Loading shimmer effect
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Hover Effects" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ ...createHoverEffect(theme, theme.palette.primary.main) }}>
              <CardContent>
                <Typography variant="h6">Hover Effect</Typography>
                <Typography variant="body2" color="text.secondary">
                  Hover over this card to see the lift and glow effect
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
              }
            }}>
              <CardContent>
                <Typography variant="h6">Scale Effect</Typography>
                <Typography variant="body2" color="text.secondary">
                  This card scales up on hover with smooth transition
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderColor: theme.palette.primary.main
              }
            }}>
              <CardContent>
                <Typography variant="h6">Color Change</Typography>
                <Typography variant="body2" color="text.secondary">
                  Background color changes on hover
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Loading States" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Skeleton Loading</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="rectangular" width="100%" height={60} />
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Progress Indicators</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <LinearProgress />
              <LinearProgress variant="determinate" value={50} />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <CircularProgress size={20} />
                <CircularProgress size={30} />
                <CircularProgress size={40} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>
    </Box>
  );

  const UtilitiesSection = () => (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontFamily: 'StrawberrySansDisplay' }}>
        Utilities & Patterns
      </Typography>
      
      <ComponentShowcase title="Glassmorphism Effects" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              ...createGlassmorphism(theme, 0.9),
              p: 3,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>High Opacity</Typography>
              <Typography variant="body2">
                Glassmorphism with 90% opacity for prominent elements
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              ...createGlassmorphism(theme, 0.6),
              p: 3,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Medium Opacity</Typography>
              <Typography variant="body2">
                Glassmorphism with 60% opacity for balanced visibility
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              ...createGlassmorphism(theme, 0.3),
              p: 3,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Low Opacity</Typography>
              <Typography variant="body2">
                Glassmorphism with 30% opacity for subtle overlays
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Background Patterns" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              ...createConstructionPattern(theme, theme.palette.warning.main),
              p: 3,
              borderRadius: 2,
              textAlign: 'center',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <Typography variant="h6" sx={{ mb: 1, position: 'relative', zIndex: 2 }}>
                Construction Pattern
              </Typography>
              <Typography variant="body2" sx={{ position: 'relative', zIndex: 2 }}>
                Animated diagonal stripes for work-in-progress areas
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              ...createDotPattern(theme, theme.palette.primary.main),
              p: 3,
              borderRadius: 2,
              textAlign: 'center',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Dot Pattern
              </Typography>
              <Typography variant="body2">
                Animated dot background for decorative elements
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              background: theme => `
                radial-gradient(circle at 90% 90%, ${alpha(theme.palette.primary.main, 0.1)}, transparent 50%),
                radial-gradient(circle at 10% 10%, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 50%),
                ${theme.palette.background.paper}
              `,
              p: 3,
              borderRadius: 2,
              textAlign: 'center',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Gradient Background
              </Typography>
              <Typography variant="body2">
                Subtle gradient overlays with theme colors
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Common Styles" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Card Styles</Typography>
            <Paper sx={{ ...COMMON_STYLES.card, p: 2 }}>
              <Typography variant="body2">
                This card uses the common card styles from the theme engine.
                It includes hover effects, proper borders, and consistent styling.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Section Title</Typography>
            <Typography sx={{ ...COMMON_STYLES.sectionTitle, mb: 2 }}>
              Section Title Style
            </Typography>
            <Typography variant="body2">
              This demonstrates the consistent section title styling used throughout
              the application with proper typography and spacing.
            </Typography>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Interactive Elements" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Toggle Buttons</Typography>
            <ToggleButtonGroup
              value={toggleValue}
              exclusive
              onChange={(event, newValue) => setToggleValue(newValue)}
            >
              <ToggleButton value="left">Left</ToggleButton>
              <ToggleButton value="center">Center</ToggleButton>
              <ToggleButton value="right">Right</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Radio Group</Typography>
            <FormControl>
              <RadioGroup value="option1">
                <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
                <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
                <FormControlLabel value="option3" control={<Radio />} label="Option 3" />
              </RadioGroup>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Stepper</Typography>
            <Stepper activeStep={activeStep} orientation="vertical">
              <Step>
                <StepLabel>Step 1</StepLabel>
              </Step>
              <Step>
                <StepLabel>Step 2</StepLabel>
              </Step>
              <Step>
                <StepLabel>Step 3</StepLabel>
              </Step>
            </Stepper>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Additional Components" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Pagination</Typography>
            <Pagination count={10} page={3} color="primary" />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Links</Typography>
            <Stack spacing={1}>
              <Link href="#" underline="hover">Hover underline</Link>
              <Link href="#" underline="always">Always underline</Link>
              <Link href="#" underline="none">No underline</Link>
            </Stack>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Collapse</Typography>
            <Button 
              onClick={() => setSwitchValue(!switchValue)}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              Toggle Collapse
            </Button>
            <Collapse in={switchValue}>
              <Alert severity="info">
                This content is collapsible! The Theme Engine includes smooth transitions
                and proper styling for all collapsible content.
              </Alert>
            </Collapse>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Update Notifications" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Centralized Update Notification</Typography>
            <Button 
              variant="contained" 
              onClick={() => setUpdateNotificationOpen(true)}
              startIcon={<NotificationsIcon />}
            >
              Show Update Notification
            </Button>
            <Alert severity="success" sx={{ mt: 2, fontSize: '0.875rem' }}>
              ✅ Centralized styling from Theme Engine<br />
              ✅ Advanced glassmorphism effects<br />
              ✅ Animated minimized indicator<br />
              ✅ Smooth transitions and hover states
            </Alert>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Features</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label="Glassmorphism Backdrop" color="primary" variant="outlined" />
              <Chip label="Gradient Background" color="primary" variant="outlined" />
              <Chip label="Shimmer Animation" color="primary" variant="outlined" />
              <Chip label="Floating Indicator" color="primary" variant="outlined" />
              <Chip label="Auto Positioning" color="primary" variant="outlined" />
              <Chip label="Theme Aware" color="primary" variant="outlined" />
            </Box>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Popup & Overlay Components" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Dialogs & Modals</Typography>
            <Stack spacing={2}>
              <Button 
                variant="contained" 
                onClick={() => setDialogOpen(true)}
              >
                Open Dialog
              </Button>
              <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                ✅ Enhanced glassmorphism effects<br />
                ✅ Improved backdrop blur<br />
                ✅ Better typography and spacing
              </Alert>
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Snackbars & Notifications</Typography>
            <Stack spacing={2}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  setSnackbarMessage('This is a sample notification with the new styling!');
                  setSnackbarOpen(true);
                }}
              >
                Show Snackbar
              </Button>
              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                ✅ Clean glassmorphism styling<br />
                ✅ Enhanced blur effects<br />
                ✅ Smooth animations
              </Alert>
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Popovers & Tooltips</Typography>
            <Stack spacing={2}>
              <Tooltip title="Enhanced tooltip with glassmorphism and improved styling" arrow>
                <Button variant="text">Hover for Tooltip</Button>
              </Tooltip>
              <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                ✅ Enhanced backdrop blur<br />
                ✅ Better contrast and readability<br />
                ✅ Smooth transitions
              </Alert>
            </Stack>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Accordion Components</Typography>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Enhanced Accordion</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  This accordion demonstrates the enhanced styling with glassmorphism effects,
                  improved borders, and smooth transitions. The Theme Engine provides consistent
                  styling for all expandable content.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </ComponentShowcase>

      <ComponentShowcase title="Advanced Dialog Components" fullWidth>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Split Dialog (CSV Import Style)</Typography>
            <Button 
              variant="contained" 
              onClick={() => setSplitDialogOpen(true)}
              startIcon={<UploadFileIcon />}
            >
              Open Split Dialog
            </Button>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Wizard Dialog</Typography>
            <Button 
              variant="contained" 
              onClick={() => setWizardDialogOpen(true)}
              startIcon={<AssignmentIcon />}
            >
              Open Wizard
            </Button>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Feature Cards</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FeatureCard
                icon={<CloudUploadIcon color="primary" sx={{ mr: 1 }} />}
                title="Cloud Upload"
                description="Upload files directly to cloud storage with automatic sync"
                onClick={() => setSnackbarMessage('Cloud upload selected')}
                clickable
              />
              
              <FeatureCard
                icon={<FolderIcon color="secondary" sx={{ mr: 1 }} />}
                title="Local Storage"
                description="Store files locally on your device for offline access"
                onClick={() => setSnackbarMessage('Local storage selected')}
                clickable
                selected={selectedFeature === 'local'}
              />
              
              <FeatureCard
                icon={<ShareIcon color="info" sx={{ mr: 1 }} />}
                title="Share & Collaborate"
                description="Share files with team members and collaborate in real-time"
                onClick={() => setSnackbarMessage('Share feature selected')}
                clickable
              />
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Upload Zone</Typography>
            <UploadZone
              accept=".pdf,.doc,.docx,.txt"
              multiple
              onFileSelect={(files) => {
                setSnackbarMessage(`Selected ${files.length} file(s)`);
                setSnackbarOpen(true);
              }}
              icon={<UploadFileIcon className="upload-icon" sx={{ fontSize: 48 }} />}
              title="Drop files here or click to select"
              subtitle={
                <DialogTip 
                  label="Supported formats:"
                  text="PDF, DOC, DOCX, TXT (max 10MB each)"
                />
              }
            />
          </Grid>
        </Grid>
      </ComponentShowcase>
    </Box>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <OverviewSection />;
      case 'layouts':
        return <LayoutsSection />;
      case 'colors':
        return <ColorPaletteSection />;
      case 'typography':
        return <TypographySection />;
      case 'components':
        return <ComponentsSection />;
      case 'animations':
        return <AnimationsSection />;
      case 'utilities':
        return <UtilitiesSection />;
      default:
        return <OverviewSection />;
    }
  };

  return (
    <SidebarScrollableLayout
      sidebarWidth="25%"
      spacing={3}
      sidebar={
        <Card sx={{ 
          ...COMMON_STYLES.card
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton 
                onClick={() => navigate('/app')}
                sx={{ 
                  mr: 2,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 500,
                  color: 'text.primary'
                }}
              >
                Theme Engine
              </Typography>
            </Box>
            
            <Typography 
              variant="overline" 
              sx={{ 
                ...COMMON_STYLES.sectionTitle,
                mb: 2,
                display: 'block'
              }}
            >
              Sections
            </Typography>
            
            <List sx={{ p: 0 }}>
              {sections.map((section) => (
                <ListItem
                  key={section.id}
                  button
                  selected={activeSection === section.id}
                  onClick={() => setActiveSection(section.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    px: 2,
                    py: 1.5,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      }
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: activeSection === section.id ? 'primary.main' : 'text.secondary',
                    minWidth: 40,
                    transition: 'color 0.2s ease'
                  }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={section.label}
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        color: activeSection === section.id ? 'primary.main' : 'text.primary',
                        fontWeight: activeSection === section.id ? 600 : 400,
                        fontSize: '0.875rem',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                size="small"
                onClick={() => setSnackbarOpen(true)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <NotificationsIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small"
                onClick={() => setDialogOpen(true)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small"
                onClick={() => navigate('/app/theme-settings')}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      }
    >
      {renderSection()}
      
      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Theme Engine Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This dialog demonstrates the theme styling for modal components.
            The Theme Engine provides consistent styling across all Material-UI components.
          </Typography>
          <TextField
            fullWidth
            label="Sample Input"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch defaultChecked />}
            label="Enable notifications"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
        sx={theme => ({
          ...ThemeStyles.snackbar(theme),
          '& .MuiAlert-root': {
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.95)} 0%, 
              ${alpha(theme.palette.primary.dark, 0.98)} 100%)`,
            color: 'white',
            border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
            '& .MuiAlert-icon': {
              color: 'white',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
            }
          }
        })}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ 
            position: 'relative',
            zIndex: 2,
            '& .MuiAlert-message': {
              fontFamily: 'StrawberrySansText',
              fontSize: '0.875rem',
              fontWeight: 500
            }
          }}
        >
          {snackbarMessage || 'Enhanced Theme Preview notification with glassmorphism effects!'}
        </Alert>
      </Snackbar>
      
      {/* Split Dialog Example */}
      <SplitDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        title="Import Data Files"
        subtitle="Upload your files to quickly update the system with new data."
        sidebarContent={
          <DialogInfoSection
            icon={<UploadFileIcon sx={{ fontSize: 80, opacity: 0.3 }} />}
            title={`Today is ${new Date().toLocaleDateString()}`}
            details={[
              'Files will be processed immediately',
              'Supported formats: CSV, JSON, XML'
            ]}
          />
        }
        mainContent={
          <>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Select File Types to Import
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              You can select multiple file types at once. The system will automatically detect and process each file type.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <FeatureCard
                icon={<AssignmentIcon color="primary" sx={{ mr: 1 }} />}
                title="Data Import"
                description="CSV files with structured data for bulk import operations"
              />
              
              <FeatureCard
                icon={<CloudUploadIcon color="secondary" sx={{ mr: 1 }} />}
                title="Configuration Files"
                description="JSON/XML configuration files for system settings"
              />
            </Box>
            
            <UploadZone
              accept=".csv,.json,.xml"
              multiple
              onFileSelect={(files) => {
                setSnackbarMessage(`Selected ${files.length} file(s) for import`);
                setSnackbarOpen(true);
                setSplitDialogOpen(false);
              }}
              icon={<UploadFileIcon className="upload-icon" sx={{ fontSize: 48 }} />}
              title="Select Files to Import"
              subtitle={
                <DialogTip 
                  label="Tip:"
                  text="Hold Ctrl (or ⌘ on Mac) to select multiple files at once"
                />
              }
            />
          </>
        }
        actions={
          <Button onClick={() => setSplitDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
        }
      />
      
      {/* Wizard Dialog Example */}
      <WizardDialog
        open={wizardDialogOpen}
        onClose={() => setWizardDialogOpen(false)}
        title="Setup Wizard"
        currentStep={activeStep}
        totalSteps={3}
        onNext={() => setActiveStep(prev => Math.min(prev + 1, 2))}
        onPrevious={() => setActiveStep(prev => Math.max(prev - 1, 0))}
        onFinish={() => {
          setSnackbarMessage('Setup completed successfully!');
          setSnackbarOpen(true);
          setWizardDialogOpen(false);
          setActiveStep(0);
        }}
      >
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" mb={2}>Step 1: Basic Information</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please provide your basic information to get started.
            </Typography>
            <TextField fullWidth label="Name" margin="normal" />
            <TextField fullWidth label="Email" margin="normal" />
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" mb={2}>Step 2: Preferences</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure your preferences and settings.
            </Typography>
            <FormControlLabel
              control={<Checkbox defaultChecked />}
              label="Enable notifications"
            />
            <FormControlLabel
              control={<Checkbox />}
              label="Auto-save changes"
            />
          </Box>
        )}
        
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" mb={2}>Step 3: Review & Confirm</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review your settings before completing the setup.
            </Typography>
            <Alert severity="info">
              Your configuration is ready to be applied.
            </Alert>
          </Box>
        )}
      </WizardDialog>

      {/* Update Notification */}
      <UpdateNotification
        open={updateNotificationOpen}
        onClose={() => setUpdateNotificationOpen(false)}
        onUpdate={() => {
          setSnackbarMessage('Update started! This would typically refresh the app with new features.');
          setSnackbarOpen(true);
          setUpdateNotificationOpen(false);
        }}
      />
    </SidebarScrollableLayout>
  );
};

export default ThemePreview; 